import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useRef, useState } from "react"
import type { UseFormReturn } from "react-hook-form"
import { useForm, useWatch } from "react-hook-form"

import { useGetShipFromTransitDays } from "./useGetShipFromTransitDays"
import { useUpdateSalesOrder } from "./useUpdateSalesOrder"

import { DateSpecialValue } from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import { type SalesOrder, SalesOrderStatus } from "@/graphql/codegen/graphql"
import { type SalesOrderLogisticsFormValues, salesOrderLogisticsFormSchema } from "@/screens/CustomerOrders/schemas"
import {
  calculateDeliveryDateFromShipDate,
  calculateShipDateFromDeliveryDate,
  toSalesOrderLogisticsFormValues,
  toSalesOrderUpdateInput,
} from "@/screens/CustomerOrders/utils"

interface UseSalesOrderLogisticsFormProps {
  salesOrder: SalesOrder
  shipToLocationId: string
  onLinesSaved?: () => void
}

interface UseSalesOrderLogisticsFormReturn {
  methods: UseFormReturn<SalesOrderLogisticsFormValues>
  handleSave: () => void
  handleSendConfirmation: () => Promise<void>
  isSaving: boolean
  handleDiscard: () => void
  isDiscardConfirmationOpen: boolean
  setIsDiscardConfirmationOpen: (isOpen: boolean) => void
  handleRequestedShipOnChange: (value: string | null) => void
  handleRequestedDeliveryDateChange: (value: string | null) => void
  handleAsapSelected: () => void
}

export const useSalesOrderLogisticsForm = ({
  salesOrder,
  shipToLocationId,
  onLinesSaved,
}: UseSalesOrderLogisticsFormProps): UseSalesOrderLogisticsFormReturn => {
  const [isDiscardConfirmationOpen, setIsDiscardConfirmationOpen] = useState(false)

  const methods = useForm<SalesOrderLogisticsFormValues>({
    defaultValues: toSalesOrderLogisticsFormValues(salesOrder),
    resolver: zodResolver(salesOrderLogisticsFormSchema),
    mode: "onChange",
  })

  const {
    formState: { isDirty },
  } = methods

  const { getTransitDays } = useGetShipFromTransitDays(shipToLocationId)

  const deliveryDate = useWatch({ control: methods.control, name: "deliveryDate" })
  const linesCount = salesOrder.lines.length
  const previousDeliveryDateRef = useRef(deliveryDate)

  // Copies the header Requested Delivery Date to every line's Deliver By Date as it's edited.
  // Compares with the last-seen value instead of dirtyFields, which can lag behind on the first edit.
  // Uses an effect because setValue() can't safely be called during render.
  useEffect(() => {
    if (previousDeliveryDateRef.current === deliveryDate) return
    previousDeliveryDateRef.current = deliveryDate
    // The ASAP sentinel has no per-line date equivalent — skip propagation until
    // SalesOrderUpdateInput gains an ASAP field.
    if (deliveryDate === DateSpecialValue.ASAP) return
    for (let index = 0; index < linesCount; index++) {
      methods.setValue(`lines.${index}.deliverDate`, deliveryDate, { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate])

  // Seeds the other requested date from the transit time on the SO's current ship-from once, the
  // first time an empty date is filled in — never on a later ship-from/date change.
  // Reading shipFromWarehouseAreaId via getValues() (a point-in-time read) rather than useWatch is
  // what makes "no recalculation on change" structural rather than a guard that could be missed.
  const handleRequestedShipOnChange = (value: string | null) => {
    if (!value || value === DateSpecialValue.ASAP) return
    if (methods.getValues("deliveryDate")) return

    const transitDays = getTransitDays(methods.getValues("shipFromWarehouseAreaId"))
    if (transitDays === null) return

    methods.setValue("deliveryDate", calculateDeliveryDateFromShipDate(value, transitDays), {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleRequestedDeliveryDateChange = (value: string | null) => {
    if (!value || value === DateSpecialValue.ASAP) return
    if (methods.getValues("requestedShipOn")) return

    const transitDays = getTransitDays(methods.getValues("shipFromWarehouseAreaId"))
    if (transitDays === null) return

    methods.setValue("requestedShipOn", calculateShipDateFromDeliveryDate(value, transitDays), {
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  // ASAP is a single "ship/deliver as soon as possible" concept spanning both requested dates,
  // not two independent flags — selecting it on either field sets both, unconditionally
  // overwriting whatever the other field currently holds.
  const handleAsapSelected = () => {
    methods.setValue("requestedShipOn", DateSpecialValue.ASAP, { shouldDirty: true, shouldValidate: true })
    methods.setValue("deliveryDate", DateSpecialValue.ASAP, { shouldDirty: true, shouldValidate: true })
  }

  const { updateSalesOrder, isSaving } = useUpdateSalesOrder()

  // Rebase the form on the saved values: the row stays mounted when the order survives a refetch,
  // and useForm only reads defaultValues once, so without this the form stays dirty (or keeps stale
  // values) and Discard would restore the values from the initial page load. A line's deliverDate can
  // move it to a different Sales Order, in which case the returned order isn't this one and the
  // refetch replaces the row instead. Same for a Ship From change, which regroups lines too.
  const rebaseForm = (savedSalesOrder: SalesOrder) => {
    if (savedSalesOrder.salesOrderId !== salesOrder.salesOrderId) return

    const savedFormValues = toSalesOrderLogisticsFormValues(savedSalesOrder)
    // Keeps the delivery-date sync effect from treating the reset as a user edit and
    // overwriting the freshly reset line dates.
    previousDeliveryDateRef.current = savedFormValues.deliveryDate
    methods.reset(savedFormValues)
  }

  const saveFormValues = async (
    formValues: SalesOrderLogisticsFormValues,
    confirmation?: { status: SalesOrderStatus; successMessage: string }
  ): Promise<void> => {
    const deliveryDateWasEdited = Boolean(methods.formState.dirtyFields.deliveryDate)
    const shipFromWasEdited = Boolean(methods.formState.dirtyFields.shipFromWarehouseAreaId)

    const updateInput = toSalesOrderUpdateInput(formValues, salesOrder.salesOrderId)
    // The ASAP sentinel is never sent, or it would be written to the Date scalar.
    if (deliveryDateWasEdited && formValues.deliveryDate !== DateSpecialValue.ASAP) {
      updateInput.deliveryDate = formValues.deliveryDate
    }
    if (shipFromWasEdited && formValues.shipFromWarehouseAreaId) {
      updateInput.lines = updateInput.lines?.map((line) => ({
        ...line,
        shipFromWarehouseAreaId: formValues.shipFromWarehouseAreaId,
      }))
    }
    if (confirmation) updateInput.status = confirmation.status

    const savedSalesOrder = confirmation
      ? await updateSalesOrder(updateInput, { successMessage: confirmation.successMessage })
      : await updateSalesOrder(updateInput)
    if (!savedSalesOrder) return

    rebaseForm(savedSalesOrder)

    // Always refetch: a save can change the status (e.g. Confirmed -> Reconfirmation Needed).
    onLinesSaved?.()
  }

  const handleSave = methods.handleSubmit(async (formValues) => {
    await saveFormValues(formValues)
  })

  // Unsaved edits travel in the same update as the status, so the BE validates the confirmation
  // against what the user sees and applies both or neither.
  const handleSendConfirmation = async () => {
    const confirmation = { status: SalesOrderStatus.Confirmed, successMessage: "Sales order confirmed" }

    if (methods.formState.isDirty) {
      await methods.handleSubmit((formValues) => saveFormValues(formValues, confirmation))()
      return
    }

    const confirmedSalesOrder = await updateSalesOrder(
      { salesOrderId: salesOrder.salesOrderId, status: confirmation.status },
      { successMessage: confirmation.successMessage }
    )
    if (!confirmedSalesOrder) return

    rebaseForm(confirmedSalesOrder)
    onLinesSaved?.()
  }

  const handleDiscard = () => {
    if (isDirty) setIsDiscardConfirmationOpen(true)
  }

  return {
    methods,
    handleSave,
    handleSendConfirmation,
    isSaving,
    handleDiscard,
    isDiscardConfirmationOpen,
    setIsDiscardConfirmationOpen,
    handleRequestedShipOnChange,
    handleRequestedDeliveryDateChange,
    handleAsapSelected,
  }
}
